import { getMasterDataForFormField, getPlaceSuggestions } from '@/services/api/applicationApi';
import type { FldListState, OptionData } from '@/types/application.types';

/**
 * Where a dropdown gets its options — mirrors Flutter OverlayDropDownBloc (_GetDrpDwnData / _GetMasterData).
 *
 *  - fields with dropDownOptions use them as-is (static)
 *  - currentLocation / locationOfEmployment → city suggestions labelled by fqLocationName (paged, searchable)
 *  - state / city                          → place suggestions (searchable)
 *  - country / nationality / countryOfEmployment → country suggestions (paged, searchable)
 *  - masterDataKey                          → generic-data lookupV2 page 1; typed search + paging only for
 *                                             specialization / educationType / qualification / typeOfIndustry
 */

export interface OptionSource {
  /** Server-side search by the typed text (otherwise the loaded list is filtered locally). */
  searchable: boolean;
  /** Further pages are fetched when the list is scrolled to the end. */
  pageable: boolean;
  load: (query: string, page: number) => Promise<OptionData[]>;
}

const LOCATION_KEYS = ['currentLocation', 'locationOfEmployment'];
const PLACE_KEYS = ['state', 'city'];
const COUNTRY_KEYS = ['country', 'nationality', 'countryOfEmployment'];
const SEARCHABLE_MASTER_KEYS = ['specialization', 'educationType', 'qualification', 'typeOfIndustry'];

export function getOptionSource(field: FldListState): OptionSource | null {
  if (field.dropDownOptions.length > 0) return null;

  if (LOCATION_KEYS.includes(field.key)) {
    return {
      searchable: true,
      pageable: true,
      load: (query, page) =>
        getPlaceSuggestions({ placeType: 'city', key: query, page, labelField: 'fqLocationName' }),
    };
  }
  if (PLACE_KEYS.includes(field.key)) {
    return {
      searchable: true,
      pageable: false,
      load: (query) => getPlaceSuggestions({ placeType: field.key as 'state' | 'city', key: query }),
    };
  }
  if (COUNTRY_KEYS.includes(field.key)) {
    return {
      searchable: true,
      pageable: true,
      load: (query, page) => getPlaceSuggestions({ placeType: 'country', key: query, page }),
    };
  }
  if (field.masterDataKey) {
    const searchable = SEARCHABLE_MASTER_KEYS.includes(field.key);
    return {
      searchable,
      pageable: searchable,
      load: (query, page) => getMasterDataForFormField(field.masterDataKey, page, searchable ? query : undefined),
    };
  }
  return null;
}
